'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { signOut, useSession } from 'next-auth/react';
import { DATA_CLEAR_SIGNAL, STRAVA_CACHE_MAX_AGE, useStore } from '@/lib/store';

export default function SessionDataGuard({ children }: { children: React.ReactNode }) {
  const { data: session, status } = useSession();
  const pathname = usePathname();
  const athleteId = status === 'authenticated' && !session?.error ? session?.athleteId ?? null : null;
  const boundAthlete = useStore(state => state.athleteId);
  const ready = useStore(state => state.isSessionReady);
  const activities = useStore(state => state.activities);
  const currentSession = useStore(state => state.currentSession);
  const bindAthlete = useStore(state => state.bindAthlete);
  const clearExpiredData = useStore(state => state.clearExpiredData);

  useEffect(() => {
    if (status !== 'loading') bindAthlete(athleteId);
  }, [status, athleteId, bindAthlete]);

  useEffect(() => {
    const onFocus = () => {
      // Re-read through the migration filter and overwrite legacy caches written
      // by a tab that was open before this release.
      void Promise.resolve(useStore.persist.rehydrate()).then(clearExpiredData);
    };
    const onStorage = (event: StorageEvent) => {
      if (event.key === DATA_CLEAR_SIGNAL) {
        // Do not broadcast again, which would cause an update loop across tabs.
        useStore.setState(state => ({ dataEpoch: state.dataEpoch + 1, activities: [], currentSession: null, lastStravaFetch: null, error: null, isProcessing: false }));
        void signOut({ redirect: false });
      } else if (event.key === 'combine-storage') {
        // Rehydrate filters legacy data; avoid a write here to prevent tab loops.
        void useStore.persist.rehydrate();
      }
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('storage', onStorage);
    };
  }, [clearExpiredData]);

  useEffect(() => {
    const times = [
      ...activities.flatMap(a => [a.stravaOwner ? a.cachedAt : undefined, a.gpxTrack?.stravaOwner ? a.gpxTrack.cachedAt : undefined]),
      ...(currentSession ? [currentSession.activity1, currentSession.activity2, currentSession.mergedTrack].map(a => a?.stravaOwner ? a.cachedAt : undefined) : []),
    ].filter((time): time is number => typeof time === 'number');
    if (!times.length) return;
    const remaining = Math.min(...times) + STRAVA_CACHE_MAX_AGE - Date.now();
    const timer = window.setTimeout(clearExpiredData, Math.max(0, remaining));
    return () => window.clearTimeout(timer);
  }, [activities, currentSession, clearExpiredData]);

  // Prevent an account change from rendering the previous athlete's data,
  // including the render before the binding effect clears the store.
  const privateDataPage = pathname === '/dashboard' || pathname === '/success' || pathname.startsWith('/merge/');
  if (privateDataPage && (status === 'loading' || !ready || athleteId !== boundAthlete)) {
    return <p role="status" className="p-6 text-center">Loading your session...</p>;
  }
  return children;
}
